import React, { ReactElement } from 'react';

import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import FilterService from '@philips-emr/hpms-provider-commons/lib/services/filter-service';
import { FieldAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { SessionStorageService } from '@philips-emr/tws-core/lib/services';

import { TechnicalVisitAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

interface TechnicalVisit {
  ieStatus: TechnicalVisitStatus,
  dsStatus: string,
  nrSeqAppointment: number
  suggestedDate: string,
  nrSeqVisit: number,
  dateTimeStamp: string,
}

enum TechnicalVisitStatus {
  PENDING = 0,
  AWAITING_APPOINTMENT = 1,
  AWAITING_CONFIRMATION = 2,
  SCHEDULED = 3,
  CLOSED = 4,
  CANCELED = 5,
  ONGOING = 6,
  ANALYSIS = 7,
  EVALUATING_INCONSISTENCIES = 8,
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT })
class TechnicalVisitController extends BaseController {

  @OnMount(Aliases.FILTER_TECHNICAL_VISIT)
  onFilterMount(event: MountEvent): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const params = {
      dtStart: '',
      dtEnd: '',
      nrSeqProvider,
    };
    FilterService.restoreFilterState(event, params);
  }

  @OnMount(Aliases.FIELD_DATE)
  onMountFieldDate({ target }: MountEvent): void {
    const api = target as FieldAPI;
    const { dateTimeStamp } = api.getData() as unknown as TechnicalVisit;
    api.setVisible(!!dateTimeStamp);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_VISIT)
  public onDynamicContentRender({ data, render }: RenderEvent): void {
    const { ieStatus, dsStatus } = data as unknown as TechnicalVisit;
    const stampVisit = this.getStamp(ieStatus, dsStatus);

    if (stampVisit) {
      render((): ReactElement => stampVisit);
    }

  }

  @OnSelectItem(Aliases.DECK_VISIT)
  onClickCardVisit(event: SelectItemEvent<TechnicalVisit>): void {
    const { nrSeqVisit, ieStatus } = event.item;
    SessionStorageService.removeItem('ieStatus');
    SessionStorageService.setItem('ieStatus', ieStatus);
    this.historyService.push(Route.TECHNICAL_VISIT_DETAILS, { nrSeqVisit });
  }

  private getStamp(ieStatus: TechnicalVisitStatus, label: string): ReactElement | null {
    let stampProps = null;

    switch (ieStatus) {
      case TechnicalVisitStatus.AWAITING_APPOINTMENT:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.AWAITING_CONFIRMATION:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
        break;
      case TechnicalVisitStatus.SCHEDULED:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
        break;
      case TechnicalVisitStatus.CLOSED:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
        break;
      case TechnicalVisitStatus.CANCELED:
        stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
        break;
      case TechnicalVisitStatus.ONGOING:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.ANALYSIS:
        stampProps = { className: 'c-form-stamp__stamp--color', icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.EVALUATING_INCONSISTENCIES:
        stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
        break;
      default:
        return null;
    }

    return <Stamp {...stampProps} />;
  }

}
export {
  TechnicalVisitController,
  TechnicalVisit,
  TechnicalVisitStatus,
};
